/**
 * CR-5362 Phase 2: per-question reviewer controls inside the
 * ReviewQuestionnaire page. Renders a status badge for the current
 * decision, three action buttons (Approve / With Concerns / Reject)
 * and an optional comment box. The questionnaire-level approve action
 * stays gated by the backend rollup, so this is the workflow the
 * reviewer steps through one question at a time.
 */
import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { CheckCircle2, AlertTriangle, XCircle, Loader2, MessageSquare, User } from 'lucide-react';
import { onboardingAPI, type FilledDatasetValue, type QuestionReviewStatus } from '../../lib/api';
import { toast } from 'sonner';
import { useFormatters } from '../../contexts/PreferencesContext';

const STATUS_CONFIG: Record<QuestionReviewStatus, { label: string; className: string }> = {
  pending_review: { label: 'Pending Review', className: 'bg-gray-100 text-gray-700' },
  approved: { label: 'Approved', className: 'bg-green-100 text-green-800' },
  approved_with_concerns: { label: 'Approved with Concerns', className: 'bg-amber-100 text-amber-800' },
  rejected: { label: 'Rejected', className: 'bg-red-100 text-red-800' },
};

interface QuestionReviewPanelProps {
  /** Current answer object — falsy when the supplier didn't answer. */
  answer: FilledDatasetValue | null | undefined;
  /** True when the questionnaire is in a state that allows review. */
  canReview: boolean;
  /** Called after a successful review POST so the parent can refetch. */
  onReviewed: (filledDatasetStatus: string) => void;
}

export default function QuestionReviewPanel({ answer, canReview, onReviewed }: QuestionReviewPanelProps) {
  const [comment, setComment] = useState(answer?.reviewer_comment ?? '');
  const { formatDateTime } = useFormatters();
  const [busy, setBusy] = useState<QuestionReviewStatus | null>(null);

  if (!answer) {
    return (
      <div className="text-xs text-muted-foreground italic mt-2">
        No answer submitted yet — review controls become available once the supplier responds.
      </div>
    );
  }

  const currentStatus: QuestionReviewStatus = (answer.question_status ?? 'pending_review') as QuestionReviewStatus;
  const cfg = STATUS_CONFIG[currentStatus];

  const handleDecision = async (decision: QuestionReviewStatus) => {
    if (!answer.id) return;
    setBusy(decision);
    try {
      const trimmed = comment.trim();
      const response =
        decision === 'approved'
          ? await onboardingAPI.approveAnswer(answer.id, trimmed)
          : decision === 'approved_with_concerns'
          ? await onboardingAPI.approveAnswerWithConcerns(answer.id, trimmed)
          : await onboardingAPI.rejectAnswer(answer.id, trimmed);
      onReviewed(response.data.filled_dataset_status);
      toast.success(
        decision === 'rejected'
          ? 'Question rejected — questionnaire sent back to supplier'
          : decision === 'approved_with_concerns'
          ? 'Question approved with concerns'
          : 'Question approved',
      );
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to record review';
      toast.error(message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-3 border-t pt-3 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <Badge className={cfg.className} variant="outline">{cfg.label}</Badge>
        {answer.reviewed_by_name && answer.reviewed_at && (
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <User className="h-3 w-3" />
            {answer.reviewed_by_name} · {formatDateTime(answer.reviewed_at)}
          </span>
        )}
      </div>

      {canReview ? (
        <>
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Add a comment (required for rejection, optional otherwise)"
            className="text-sm"
            rows={2}
            disabled={busy !== null}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              className="text-green-700 border-green-300 hover:bg-green-50"
              disabled={busy !== null}
              onClick={() => handleDecision('approved')}
            >
              {busy === 'approved' ? (
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              ) : (
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
              )}
              Approve
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="text-amber-700 border-amber-300 hover:bg-amber-50"
              disabled={busy !== null}
              onClick={() => handleDecision('approved_with_concerns')}
            >
              {busy === 'approved_with_concerns' ? (
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              ) : (
                <AlertTriangle className="h-3.5 w-3.5 mr-1" />
              )}
              With Concerns
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="text-red-700 border-red-300 hover:bg-red-50"
              disabled={busy !== null || !comment.trim()}
              title={!comment.trim() ? 'Add a comment explaining the rejection' : undefined}
              onClick={() => handleDecision('rejected')}
            >
              {busy === 'rejected' ? (
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              ) : (
                <XCircle className="h-3.5 w-3.5 mr-1" />
              )}
              Reject
            </Button>
          </div>
        </>
      ) : (
        answer.reviewer_comment && (
          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <MessageSquare className="h-3 w-3 mt-0.5 shrink-0" />
            <p>{answer.reviewer_comment}</p>
          </div>
        )
      )}
    </div>
  );
}
